import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { apiOrigin } from '@/utils/apiBase';

const API = apiOrigin();
const WELCOME_POLL = 2500;   // poll every 2.5s for new arrivals
const META_POLL = 60000;     // refresh event meta every 60s
const SHOW_FOR = 5500;       // each welcome animation lasts 5.5s

/**
 * Welcome Wall — full-screen entrance display.
 *
 * Lives at /party/:slug/welcome-wall. No auth required.
 * Polls the same kiosk_welcomes queue used by the regular Kiosk
 * spotlight, but renders ONLY the welcome animation full-screen —
 * designed to be left running on a tablet/laptop at the door.
 *
 * Idle state: pulsing event emoji + "Welcome to <Event>" + ambient sparkles.
 * Active state: gigantic guest name, theme-colored radial burst,
 * floating emoji sparkles, "Welcome, <Name>! <emoji>".
 */
export default function WelcomeWallPage() {
  const { slug } = useParams();
  const [meta, setMeta] = useState(null);
  const [err, setErr] = useState('');
  const [active, setActive] = useState(null); // { names: [...], key, source }
  const [counter, setCounter] = useState(0);  // total arrivals seen this session
  const [lastAt, setLastAt] = useState(null);
  const lastWelcomeIdRef = useRef('');
  const queueRef = useRef([]);
  const timerRef = useRef(null);
  const playingRef = useRef(false);
  const firstLoadDoneRef = useRef(false);

  // Load event meta (title, theme) once + refresh occasionally
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const r = await axios.get(`${API}/api/events/public/${slug}/kiosk`);
        if (cancelled) return;
        setMeta(r.data?.event || null);
        setErr('');
      } catch (e) {
        if (!cancelled) setErr(e.response?.data?.detail || 'Event not found');
      }
    };
    load();
    const id = setInterval(load, META_POLL);
    return () => { cancelled = true; clearInterval(id); };
  }, [slug]);

  const playNext = () => {
    if (queueRef.current.length === 0) {
      playingRef.current = false;
      setActive(null);
      return;
    }
    const next = queueRef.current.shift();
    playingRef.current = true;
    setActive({ names: next.names, key: Date.now(), source: next.source });
    setCounter((c) => c + 1);
    setLastAt(new Date());
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      setActive(null);
      // Brief gap between welcomes, then drain queue
      setTimeout(() => {
        playingRef.current = false;
        playNext();
      }, 350);
    }, SHOW_FOR);
  };

  // Poll the welcomes queue
  useEffect(() => {
    let cancelled = false;
    // Baseline: snapshot current queue cursor so we don't replay historical welcomes
    (async () => {
      try {
        const r = await axios.get(`${API}/api/events/public/${slug}/kiosk/welcomes`);
        const items = r.data?.welcomes || [];
        if (items.length > 0) {
          lastWelcomeIdRef.current = items[items.length - 1].welcome_id;
        }
      } catch (e) { /* silent */ }
      firstLoadDoneRef.current = true;
    })();

    const poll = async () => {
      if (!firstLoadDoneRef.current) return;
      try {
        const r = await axios.get(`${API}/api/events/public/${slug}/kiosk/welcomes`, {
          params: lastWelcomeIdRef.current ? { since: lastWelcomeIdRef.current } : {},
        });
        if (cancelled) return;
        const items = r.data?.welcomes || [];
        if (items.length === 0) return;
        for (const w of items) {
          lastWelcomeIdRef.current = w.welcome_id || lastWelcomeIdRef.current;
          queueRef.current.push({ names: w.names || [], source: w.source || 'queue' });
        }
        if (!playingRef.current) playNext();
      } catch (e) { /* silent */ }
    };

    const id = setInterval(poll, WELCOME_POLL);
    return () => {
      cancelled = true;
      clearInterval(id);
      if (timerRef.current) clearTimeout(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  // Press 'T' to trigger a demo welcome (handy for testing on the actual tablet)
  useEffect(() => {
    const onKey = (e) => {
      if (e.key?.toLowerCase() === 't') {
        queueRef.current.push({ names: ['Test Guest 🎉'], source: 'manual' });
        if (!playingRef.current) playNext();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (err) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black text-white p-8">
        <p className="text-xl">{err}</p>
      </div>
    );
  }

  const themeColor = meta?.theme_color || '#FF6B35';
  const themeEmoji = meta?.theme_emoji || '🎉';
  const title = meta?.title || 'Welcome';

  return (
    <div
      data-testid="welcome-wall"
      className="fixed inset-0 overflow-hidden flex flex-col items-center justify-center select-none"
      style={{
        background: `radial-gradient(ellipse at center, ${themeColor}22 0%, #000 70%)`,
        color: 'white',
      }}
    >
      {/* IDLE STATE */}
      {!active && (
        <div className="flex flex-col items-center justify-center text-center px-8 ww-idle-fade">
          <div className="ww-idle-emoji" style={{ filter: `drop-shadow(0 0 30px ${themeColor}88)` }}>
            {themeEmoji}
          </div>
          <div className="ww-overline mt-6" style={{ color: themeColor }}>
            ✨ Welcome to ✨
          </div>
          <h1 className="ww-title mt-3">{title}</h1>
          <p className="ww-sub mt-6">Scan your QR badge on arrival</p>
          {/* Ambient floating sparkles */}
          <div className="ww-ambient" aria-hidden="true">
            {Array.from({ length: 14 }).map((_, i) => (
              <span key={i} className="ww-amb-emoji"
                style={{ '--i': i, left: `${(i * 73) % 100}%`, animationDelay: `${(i * 0.7) % 8}s` }}>
                {[themeEmoji, '✨', '⭐'][(i + 1) % 3]}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* ACTIVE WELCOME */}
      {active && (
        <div key={active.key} className="absolute inset-0 flex flex-col items-center justify-center text-center px-8 ww-active"
          data-testid="welcome-wall-active">
          {/* Radial color wash */}
          <div className="ww-burst" style={{ background: `radial-gradient(circle at center, ${themeColor}66 0%, ${themeColor}22 30%, transparent 70%)` }} />

          {/* Bouncing big emoji */}
          <div className="ww-active-emoji" style={{ filter: `drop-shadow(0 0 60px ${themeColor})` }}>
            {themeEmoji}
          </div>

          {/* "WELCOME" header */}
          <div className="ww-welcome-tag" style={{ color: themeColor }}>WELCOME</div>

          {/* Names */}
          <div className="ww-names" style={{ textShadow: `0 0 60px ${themeColor}, 0 0 16px ${themeColor}` }}
            data-testid="welcome-wall-names">
            {active.names.join(' & ')}
          </div>

          {/* Subtitle */}
          <div className="ww-subtitle">
            {active.source === 'qr_checkin' ? 'Checked in! Enjoy the party 🥳' : 'So glad you made it 🥳'}
          </div>

          {/* Floating sparkle emojis */}
          <div className="ww-sparkles" aria-hidden="true">
            {Array.from({ length: 24 }).map((_, i) => (
              <span key={i} className="ww-sparkle"
                style={{
                  left: `${(i * 41 + 5) % 95}%`,
                  top: `${10 + ((i * 17) % 80)}%`,
                  animationDelay: `${(i * 0.12)}s`,
                  fontSize: `${22 + ((i * 7) % 24)}px`,
                }}>
                {[themeEmoji, '✨', '🎉', '⭐', '💫', '🪩'][i % 6]}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Footer: live indicator + counter */}
      <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between text-white/40 text-xs font-mono uppercase tracking-widest ww-footer">
        <div className="flex items-center gap-2">
          <span className="ww-live-dot" />
          LIVE · {title}
        </div>
        <div className="text-right">
          <div data-testid="welcome-wall-counter">Arrivals this session: <span className="text-white/80 font-bold">{counter}</span></div>
          {lastAt && <div className="opacity-60">Last: {lastAt.toLocaleTimeString()}</div>}
        </div>
      </div>

      <style>{`
        body, html { overflow: hidden; }
        .ww-idle-emoji { font-size: 18vw; line-height: 1; animation: ww-bob 4s ease-in-out infinite; }
        .ww-overline { font-size: 1.4vw; letter-spacing: 0.4em; font-weight: 700; text-transform: uppercase; }
        .ww-title { font-size: 7vw; font-weight: 900; line-height: 1; letter-spacing: -0.02em; max-width: 90vw; }
        .ww-sub { font-size: 1.6vw; color: rgba(255,255,255,0.55); letter-spacing: 0.06em; }
        .ww-idle-fade { animation: ww-fade-in 0.6s ease both; }
        @keyframes ww-bob { 0%,100%{ transform: translateY(0) rotate(-2deg); } 50%{ transform: translateY(-18px) rotate(2deg); } }
        @keyframes ww-fade-in { from { opacity: 0; transform: scale(0.96);} to { opacity: 1; transform: scale(1);} }

        .ww-ambient { position: absolute; inset: 0; pointer-events: none; }
        .ww-amb-emoji {
          position: absolute; bottom: -10vh; font-size: 32px; opacity: 0.35;
          animation: ww-rise 14s linear infinite;
        }
        @keyframes ww-rise {
          0% { transform: translateY(0) rotate(0deg); opacity: 0; }
          10% { opacity: 0.45; }
          90% { opacity: 0.3; }
          100% { transform: translateY(-120vh) rotate(360deg); opacity: 0; }
        }

        .ww-active { animation: ww-active-in 0.55s cubic-bezier(0.18, 1.6, 0.5, 1); }
        @keyframes ww-active-in { from { opacity: 0; transform: scale(0.85);} to { opacity: 1; transform: scale(1);} }

        .ww-burst {
          position: absolute; inset: 0; pointer-events: none;
          animation: ww-burst-in 1.2s ease-out;
        }
        @keyframes ww-burst-in { 0%{ opacity: 0; transform: scale(0.7); } 60%{ opacity: 1; } 100%{ opacity: 1; transform: scale(1.05);} }

        .ww-active-emoji {
          font-size: 16vw; line-height: 1;
          animation: ww-bounce 0.9s cubic-bezier(0.18, 1.6, 0.5, 1) infinite alternate;
          position: relative; z-index: 2;
        }
        @keyframes ww-bounce {
          0% { transform: translateY(0) scale(1) rotate(-4deg);}
          100% { transform: translateY(-22px) scale(1.06) rotate(4deg);}
        }

        .ww-welcome-tag {
          font-size: 1.8vw; letter-spacing: 0.5em; font-weight: 900;
          margin-top: 2vh; padding-left: 0.5em;
          position: relative; z-index: 2;
          animation: ww-slide-in 0.6s 0.15s both ease;
        }

        .ww-names {
          font-size: 9vw; font-weight: 900; line-height: 1.05; max-width: 95vw;
          letter-spacing: -0.02em; margin-top: 1.5vh; word-break: break-word;
          position: relative; z-index: 2;
          animation: ww-slide-in 0.7s 0.25s both ease;
        }

        .ww-subtitle {
          font-size: 1.8vw; color: rgba(255,255,255,0.78); margin-top: 2vh;
          position: relative; z-index: 2;
          animation: ww-slide-in 0.7s 0.5s both ease;
        }
        @keyframes ww-slide-in {
          from { opacity: 0; transform: translateY(40px);}
          to { opacity: 1; transform: translateY(0);}
        }

        .ww-sparkles { position: absolute; inset: 0; pointer-events: none; z-index: 1; }
        .ww-sparkle {
          position: absolute; animation: ww-spk 2.6s ease-out forwards;
          opacity: 0;
        }
        @keyframes ww-spk {
          0% { transform: translateY(0) rotate(0deg) scale(0.4); opacity: 0; }
          18% { opacity: 1; }
          100% { transform: translateY(-260px) rotate(540deg) scale(1.3); opacity: 0; }
        }

        .ww-footer { z-index: 3; }
        .ww-live-dot {
          width: 8px; height: 8px; border-radius: 50%; background: #22C55E;
          box-shadow: 0 0 12px #22C55E;
          animation: ww-pulse 1.4s ease-in-out infinite;
        }
        @keyframes ww-pulse { 0%,100%{ opacity: 0.5;} 50%{ opacity: 1;} }
      `}</style>
    </div>
  );
}
